import { yupResolver } from '@hookform/resolvers/yup';
import { IonText } from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import ExpandableSection from '@components/animations/ExpandableSection';
import ActionIconButton from '@components/buttons/ActionIconButton';
import CircleIconButton from '@components/buttons/CircleIconButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import DateAndTimeStamp from '@components/dates/DateAndTimeStamp';
import DateStamp from '@components/dates/DateStamp';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import BaseErrors from '@components/fields/BaseErrors';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Form from '@components/forms/Form';
import InternalNoteForm from '@components/forms/InternalNoteForm';
import Modal from '@components/Modal';
import Nbsp from '@components/Nbsp';
import styles from '@components/profile/Profile.module.css';
import MiniCopy from '@components/text/MiniCopy';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading2 from '@components/typography/Heading2';
import useCreateInternalNote from '@hooks/mutations/useCreateInternalNote';
import useDestroyInternalNote from '@hooks/mutations/useDestroyInternalNote';
import useFormPersist from '@hooks/useFormPersist';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { GetJourneyWithCareProfileQuery, GetJourneyWithExpertPlanQuery, InternalNoteKindEnum } from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { sortByTimestamp } from '@utils/modelUtils';

type FormData = {
  internalNoteBody: string | null;
  planBody: string | null;
  synopsisBody: string | null;
  title: string | null;
};

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
  onlySynopsis?: boolean;
};

type Note = NonNullable<GetJourneyWithExpertPlanQuery['journey']['internalNotes']>[0];

type InternalNoteProps = {
  defaultOpen?: boolean;
  handleDelete: (note: Note) => void;
  handleEdit: (note: Note) => void;
  journeyId: string;
  note: Note;
};

const InternalNote = ({ defaultOpen = false, handleDelete, handleEdit, journeyId, note }: InternalNoteProps) => {
  const opener = useOpener(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  const hasBeenUpdated = Math.abs(note.createdAt - note.updatedAt) >= 1000;

  const sanitizedBody = useSanitizeHtml(note.body, journeyId, note);

  return (
    <div className={styles.internalNoteContainer}>
      <div className={styles.internalNote} onClick={opener.toggle} ref={ref}>
        <div>
          <BodyCopy color="neutral500">
            <FormattedMessage
              id={
                note.kind === InternalNoteKindEnum.NOTE
                  ? 'pages.carePlan.base.expert.internalNotesDate'
                  : 'pages.carePlan.base.expert.versionHistoryDate'
              }
              values={{ date: <DateAndTimeStamp value={note.createdAt} /> }}
            />
          </BodyCopy>
          {note.title && (
            <BodyCopyBold color="primary700">
              <Nbsp />
              <Nbsp />
              {note.title}
            </BodyCopyBold>
          )}

          <Nbsp />
          {hasBeenUpdated && (
            <IonText className={styles.edited}>
              <FormattedMessage
                id="pages.carePlan.base.expert.edited"
                values={{ date: <DateStamp showWeekday={false} value={note.updatedAt} /> }}
              />
            </IonText>
          )}
        </div>
      </div>
      <ExpandableSection initial={false} isOpen={opener.isOpen}>
        <div className={styles.internalNoteBody}>{sanitizedBody}</div>
      </ExpandableSection>

      <div className={opener.isOpen ? styles.actions : 'ion-hide'}>
        {note.kind === InternalNoteKindEnum.NOTE && (
          <>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.delete"
              icon="trash"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete(note);
              }}
            />
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.edit"
              icon="edit"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleEdit(note);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

const ExpertInternalNotes = ({ journey, onlySynopsis = false }: Props) => {
  const [section, setSection] = useState(onlySynopsis ? InternalNoteKindEnum.SYNOPSIS : InternalNoteKindEnum.NOTE);
  const editOpener = useOpener();
  const planOpener = useOpener();
  const versionHistoryOpener = useOpener();

  const initialValues: FormData = {
    internalNoteBody: '',
    planBody: '',
    synopsisBody: '',
    title: null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [noteBeingDeleted, setNoteBeingDeleted] = useState<Note | undefined>(undefined);
  const [editorRefresh, setEditorRefresh] = useState(false);
  const deleteOpener = useOpener();
  const internalNotes = sortByTimestamp(
    journey.internalNotes?.filter(note => note.kind === section) ?? [],
    i => i.createdAt
  );

  const createInternalNote = useCreateInternalNote({ journeyId: journey.id });

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      internalNoteBody:
        section === InternalNoteKindEnum.NOTE
          ? yup.string().required(intl.formatMessage({ id: 'errors.internalNote.body.blank' }))
          : yup.string().nullable().defined(),
      planBody:
        section === InternalNoteKindEnum.ROADMAP
          ? yup.string().required(intl.formatMessage({ id: 'errors.internalNote.body.blank' }))
          : yup.string().nullable().defined(),
      synopsisBody:
        section === InternalNoteKindEnum.SYNOPSIS
          ? yup.string().required(intl.formatMessage({ id: 'errors.internalNote.body.blank' }))
          : yup.string().nullable().defined(),
      title: yup.string().nullable().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const exclude = onlySynopsis ? ['internalNoteBody', 'planBody', 'title'] : ['synopsisBody'];

  const formPersist = useFormPersist(onlySynopsis ? 'expertInetnalNotesSynopsisForm' : 'expertInternalNotesForm', {
    exclude,
    form,
    onDataRestored: () => {
      setEditorRefresh(!editorRefresh);
    },
    uniqueId: journey.id
  });

  // This horrible thing is necessary because changing the section doesn't seem to properly trigger the
  // validations, which are based on it. The errors on the form would always be based on the previous
  // value of the section unless I force it to update like this.
  useEffect(() => {
    if (section === InternalNoteKindEnum.NOTE) {
      setTimeout(() => {
        form.trigger('planBody');
        form.trigger('synopsisBody');
      }, 100);
    } else if (section === InternalNoteKindEnum.ROADMAP) {
      setTimeout(() => {
        form.trigger('internalNoteBody');
        form.trigger('synopsisBody');
      }, 100);
    } else if (section === InternalNoteKindEnum.SYNOPSIS) {
      setTimeout(() => {
        form.trigger('internalNoteBody');
        form.trigger('planBody');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const mostRecentNote = internalNotes[0];
  const mostRecentNoteBody = mostRecentNote?.body;

  useEffect(() => {
    if (planOpener.isOpen && mostRecentNoteBody) {
      if (section === InternalNoteKindEnum.ROADMAP && !form.getValues().planBody) {
        form.setValue('planBody', mostRecentNoteBody, { shouldTouch: true, shouldValidate: true });
        setEditorRefresh(!editorRefresh);
      } else if (section === InternalNoteKindEnum.SYNOPSIS && !form.getValues().synopsisBody) {
        form.setValue('synopsisBody', mostRecentNoteBody, { shouldTouch: true, shouldValidate: true });
        setEditorRefresh(!editorRefresh);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, planOpener.isOpen, mostRecentNoteBody]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    let body: string | null = '';
    if (section === InternalNoteKindEnum.NOTE) {
      body = form.getValues().internalNoteBody;
    } else if (section === InternalNoteKindEnum.ROADMAP) {
      body = form.getValues().planBody;
    } else {
      body = form.getValues().synopsisBody;
    }

    if (!body) return;

    const values = {
      body,
      journeyId: journey.id,
      kind: section,
      title: section === InternalNoteKindEnum.NOTE ? form.getValues().title : null
    };

    return createInternalNote(values).then(response => {
      handleMutationResponse<FormData>({
        data: response?.internalNoteCreate,
        intl,
        onSuccess: () => {
          formPersist.clear();
          planOpener.close();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.internalNote'
      });
      if (section !== InternalNoteKindEnum.ROADMAP) {
        form.reset();
      }
    });
  }, [createInternalNote, form, journey.id, section, intl, formPersist, planOpener]);

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    editOpener.open();
  };

  const destroyInternalNote = useDestroyInternalNote({ journeyId: journey.id });

  const handleDelete = useCallback(() => {
    if (noteBeingDeleted) {
      destroyInternalNote({ id: noteBeingDeleted.id });
    }
    setNoteBeingDeleted(undefined);
    deleteOpener.close();
  }, [deleteOpener, destroyInternalNote, noteBeingDeleted]);

  const displayedInternalNotes = useMemo(() => {
    if (section === InternalNoteKindEnum.NOTE) {
      return internalNotes;
    }

    return internalNotes.slice(1);
  }, [internalNotes, section]);

  const sanitizedMostRecentBody = useSanitizeHtml(mostRecentNoteBody, journey.id, mostRecentNote);

  return (
    <div>
      <div className={onlySynopsis ? 'ion-hide' : styles.internalNoteSections}>
        {[InternalNoteKindEnum.NOTE, InternalNoteKindEnum.ROADMAP].map(kind => (
          <div
            className={section === kind ? styles.selectedInternalNoteSection : styles.internalNoteSection}
            key={kind}
            onClick={() => {
              setSection(kind);
            }}
          >
            <Heading2>
              <FormattedMessage id={`pages.carePlan.base.expert.noteSections.${kind}`} />
            </Heading2>
          </div>
        ))}
      </div>

      <div>
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <BaseErrors errors={baseErrors} />

          {onlySynopsis && (
            <div className={styles.synopsisHeader}>
              <Heading2>
                <FormattedMessage id={`pages.carePlan.base.expert.noteSections.${section}`} />
              </Heading2>
              <div className={styles.synopsisEditIcon}>
                <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={planOpener.open} />
              </div>
            </div>
          )}
          <div
            className={
              planOpener.isOpen || section === InternalNoteKindEnum.NOTE ? styles.relativeContainer : 'ion-hide'
            }
          >
            <div className={section === InternalNoteKindEnum.NOTE ? '' : 'ion-hide'}>
              <Input<FormData>
                contentUnder
                control={form.control}
                error={form.formState.errors.title}
                name="title"
                noSpacing
                placeholder={intl.formatMessage({ id: 'pages.carePlan.base.expert.title' })}
              />
              <RichTextArea<FormData>
                contentRefreshSwitch={editorRefresh}
                control={form.control}
                error={form.formState.errors.internalNoteBody}
                extraStyles={styles.editor}
                journeyId={journey.id}
                name="internalNoteBody"
                placeholder={intl.formatMessage({ id: 'pages.carePlan.base.expert.typeNotesHere' })}
                sharableURLSHidden
                size="large"
              />
            </div>
            <div className={section === InternalNoteKindEnum.ROADMAP ? '' : 'ion-hide'}>
              <RichTextArea<FormData>
                contentRefreshSwitch={editorRefresh}
                control={form.control}
                error={form.formState.errors.planBody}
                extraStyles={styles.editor}
                journeyId={journey.id}
                name="planBody"
                placeholder={intl.formatMessage({ id: 'pages.carePlan.base.expert.typeNotesHere' })}
                sharableURLSHidden
                size="large"
              />
            </div>
            <div className={onlySynopsis ? styles.synopsisContent : 'ion-hide'}>
              <RichTextArea<FormData>
                contentRefreshSwitch={editorRefresh}
                control={form.control}
                error={form.formState.errors.synopsisBody}
                extraStyles={styles.editor}
                journeyId={journey.id}
                name="synopsisBody"
                placeholder={intl.formatMessage({ id: 'pages.carePlan.base.expert.typeNotesHere' })}
                sharableURLSHidden
                size="small"
              />
            </div>

            <div className={styles.submitIcons}>
              <CircleIconButton
                ariaLabelKey="accessibility.ariaLabels.add"
                color="success300"
                disabled={!form?.formState.isValid || form?.formState.isSubmitting}
                icon="checkmark"
                type="submit"
              />
              <CircleIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                color="error300"
                icon="xmark"
                onClick={() => {
                  if (section === InternalNoteKindEnum.ROADMAP || section === InternalNoteKindEnum.SYNOPSIS) {
                    planOpener.toggle();
                  } else {
                    form.reset();
                  }
                }}
              />
            </div>
          </div>
          <div
            className={`${
              !planOpener.isOpen && section !== InternalNoteKindEnum.NOTE ? styles.editablePlan : 'ion-hide'
            } ${onlySynopsis ? styles.onlySynopsisContainer : ''}`}
            onClick={planOpener.toggle}
          >
            {mostRecentNote && (
              <div>
                <MiniCopy color="neutral500">
                  <DateAndTimeStamp value={mostRecentNote.createdAt} />
                </MiniCopy>
              </div>
            )}
            {mostRecentNoteBody && <>{sanitizedMostRecentBody}</>}

            <div className={styles.editIcon}>
              <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" />
            </div>
          </div>
        </Form>
        {onlySynopsis && displayedInternalNotes.length > 0 && (
          <TertiaryButton
            icon={versionHistoryOpener.isOpen ? 'chevronUp' : 'chevronDown'}
            onClick={versionHistoryOpener.toggle}
            textKey={
              versionHistoryOpener.isOpen
                ? 'models.internalNote.hideVersionHistory'
                : 'models.internalNote.showVersionHistory'
            }
          />
        )}
        <div className={!onlySynopsis || versionHistoryOpener.isOpen ? styles.internalNotes : 'ion-hide'}>
          {/* if the section is not equal to note, ignore the first internal note */}

          {displayedInternalNotes.map((note, index) => (
            <InternalNote
              defaultOpen={index === 0 && section === InternalNoteKindEnum.NOTE}
              handleDelete={note => {
                setNoteBeingDeleted(note);
                deleteOpener.open();
              }}
              handleEdit={handleEdit}
              journeyId={journey.id}
              key={note.id}
              note={note}
            />
          ))}
        </div>
      </div>

      <DeleteConfirmationAlert
        messageKey="models.internalNote.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
      <Modal opener={editOpener} title={intl.formatMessage({ id: 'models.internalNote.editInternalNote' })}>
        {() => (
          <InternalNoteForm
            internalNote={editingNote}
            journeyId={journey.id}
            onCancel={editOpener.close}
            onSuccess={editOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default ExpertInternalNotes;
